<template>
  <header class="app-header">
    <div class="header-content">
      <nav class="nav">
        <router-link
          :to="getNavPath('/all')"
          class="nav-item"
          :class="{ active: isActive('/all') || isActive('/next-draws') }"
        >
          <span class="nav-link">{{ $t('nav.all') }}</span>
        </router-link>
        <router-link
          :to="getNavPath('/lucky-numbers')"
          class="nav-item"
          :class="{ active: isActive('/lucky-numbers') }"
        >
          <span class="nav-link">{{ $t('nav.luckyNumbers') }}</span>
        </router-link>
        <router-link
          v-if="showWagers"
          :to="getNavPath('/wagers')"
          class="nav-item"
          :class="{ active: isActive('/wagers') }"
        >
          <span class="nav-link">{{ $t('nav.wagers') }}</span>
        </router-link>
        <router-link
          :to="getNavPath('/results')"
          class="nav-item"
          :class="{ active: isActive('/results') }"
        >
          <span class="nav-link">{{ $t('nav.results') }}</span>
        </router-link>
        <router-link
          :to="getNavPath('/how-to-play')"
          class="nav-item"
          :class="{ active: isActive('/how-to-play') }"
        >
          <span class="nav-link">{{ $t('nav.howToPlay') }}</span>
        </router-link>
        
        <div class="nav-trailing">
          <div
            v-if="showThemeToggle"
            class="theme-nav-item"
          >
            <ThemeToggle layout="nav" />
          </div>
          <div
            class="cart-nav-item"
            @click="openBetslip"
          >
            <div class="cart-wrapper">
              <img
                src="/img/icons/cart.svg"
                alt=""
                class="cart-svg"
                aria-hidden="true"
              >
              <span
                v-if="betCount > 0"
                class="bet-count"
              >{{ betCount }}</span>
            </div>
          </div>
        </div>
      </nav>
    </div>
  </header>
</template>

<script setup lang="ts">
import { onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '../../stores/auth'
import { useBetslipStore } from '../../stores/betslip'
import { useModalStore } from '../../stores/modal'
import { useTheme } from '../../composables/useTheme'
import { useIntegrationMode } from '@/composables/useIntegrationMode'
import { useThemeStore } from '@/stores/theme'
import ThemeToggle from '@/components/ThemeToggle.vue'

const route = useRoute()
const authStore = useAuthStore()
const betslipStore = useBetslipStore()
const modalStore = useModalStore()
const { currentTheme } = useTheme()
const themeStore = useThemeStore()
const { isLite } = useIntegrationMode()

const betCount = computed(() => betslipStore.betslip?.length || 0)

const showThemeToggle = computed(() => {
  if (isLite.value) return false
  // Single-mode themes (forceColorMode) never show the toggle.
  if (themeStore.lockedColorMode) return false
  const navigation = currentTheme.value?.navigation
  if (navigation?.hideThemeToggle) return false
  if (navigation?.forceColorMode) return false
  return true
})

const isActive = (path: string) => route.path === path

const openBetslip = () => {
  modalStore.openBetslip()
}

const getNavPath = (path: string) => {
  return { path, query: route.query }
}

const showWagers = computed(() => {
  const hideWhenUnauthenticated = currentTheme.value?.navigation?.hideWagersWhenUnauthenticated
  if (hideWhenUnauthenticated) {
    return authStore.isAuthenticated || authStore.hasValidSession()
  }
  return true
})

onMounted(() => {
  authStore.initializeFromSession()
})
</script>

<style scoped>
.app-header {
  width: 100%;
  margin-bottom: var(--app-header-margin-bottom, 8px);
}

.header-content {
  background: var(--app-header-bg, var(--colour-surface-cont-low));
  width: 100%;
  border: var(--app-header-border-width, 0) solid var(--app-header-border-color, transparent);
  border-radius: var(--app-header-radius, 0);
  box-shadow: var(--app-header-shadow, none);
  overflow: hidden;
}

.nav {
  display: flex;
  align-items: stretch;
  height: var(--app-header-nav-height, 60px);
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.nav::-webkit-scrollbar {
  display: none;
}

.nav-item,
.nav-item:link,
.nav-item:visited,
.nav-item:active,
.nav-item:focus {
  flex: var(--app-header-nav-item-flex, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  color: inherit;
  border-right: var(--app-header-divider-width, 1px) solid var(--app-header-divider-color, var(--colour-surface-cont));
  border-left: var(--app-header-divider-width, 1px) solid transparent;
  /* border-bottom: var(--app-header-inactive-bottom-border-width, 1px) solid var(--app-header-divider-color, var(--colour-surface-cont)); */
  background: var(--bg-nav-item, transparent);
  min-width: var(--app-header-nav-item-min-width, 120px);
  position: relative;
  transition: border-color 0.15s ease;
  outline: none;
  -webkit-tap-highlight-color: transparent;
}

.nav-item:hover {
  background: var(--bg-nav-item-hover, var(--tab-button-inactive));
}

.nav-item:hover .nav-link {
  color: var(--app-header-hover-link-color, var(--app-header-link-color, var(--colour-text-inverse)));
}

.nav-link {
  color: var(--app-header-link-color, var(--bg-nav-item-color, var(--colour-text-inverse)));
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: var(--typography-font-size-large, 18px);
  font-weight: var(--app-header-link-weight, var(--typography-font-weight-normal, 400));
  text-align: center;
  line-height: 1;
  white-space: nowrap;
  text-decoration: none;
}

.nav-item.active,
.nav-item.router-link-active {
  background: var(--app-header-active-bg, var(--bg-nav-item, transparent));
  border-left-color: var(--app-header-active-divider-color, transparent);
  border-right-color: var(--app-header-active-divider-color, var(--app-header-divider-color, var(--colour-surface-cont)));
  border-bottom: var(--app-header-active-bottom-border-width, 3px) solid var(--app-header-active-bottom-border-color, var(--colour-orange-button));
}

.nav-item.active .nav-link,
.nav-item.router-link-active .nav-link {
  color: var(--app-header-active-link-color, var(--colour-orange-text-colour));
  font-weight: var(--app-header-active-link-weight, 700);
}

.nav-trailing {
  display: flex;
  align-items: stretch;
  flex-shrink: 0;
  position: sticky;
  right: 0;
  z-index: 10;
  box-shadow: var(--app-header-cart-shadow, -2px 0 5px var(--shadow-color));
  border-left: var(--app-header-divider-width, 1px) solid var(--app-header-divider-color, var(--colour-surface-cont));
}

.theme-nav-item,
.cart-nav-item {
  background: var(--colour-surface-cont-lowest);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cart-nav-item {
  width: 80px;
  cursor: pointer;
  border-bottom: var(--app-header-inactive-bottom-border-width, 1px) solid var(--app-header-divider-color, var(--colour-surface-cont));
}

.theme-nav-item {
  width: var(--app-header-theme-nav-width, 60px);
  cursor: pointer;
  border-bottom: none;
  border-right: var(--app-header-divider-width, 1px) solid var(
    --app-header-trailing-divider-color,
    var(--colour-surface-cont)
  );
}

.cart-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cart-svg {
  width: 24px;
  height: 24px;
  display: block;
  filter: var(--cart-icon-filter, none);
}

.bet-count {
  position: absolute;
  top: -8px;
  right: -8px;
  background: var(--bg-bet-count, var(--colour-orange-button));
  color: var(--text-bet-count, var(--bet-count-color, var(--text-button)));
  font-size: 10px;
  font-weight: bold;
  min-width: 18px;
  min-height: 18px;
  aspect-ratio: 1;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  box-sizing: border-box;
  line-height: 1;
}

@media (max-width: 1024px) {
  .nav-link {
    font-size: 16px;
  }
  .nav-item {
    min-width: var(--app-header-nav-item-tablet-min-width, var(--app-header-nav-item-min-width, 100px));
  }
}

@media (max-width: 768px) {
  .app-header {
    margin-bottom: var(--app-header-mobile-margin-bottom, 4px);
  }

  .nav {
    height: 40px;
  }

  .nav-trailing {
    display: flex;
    align-items: stretch;
  }
  
  .nav-item {
    flex: var(--app-header-mobile-nav-item-flex, var(--app-header-nav-item-flex, 1));
    min-width: var(--app-header-mobile-nav-item-min-width, 64px);
    width: var(--app-header-mobile-nav-item-width, auto);
    padding: var(--app-header-mobile-nav-item-padding, 0 4px);
  }
  
  .nav-link {
    font-size: 11px;
  }
  
  .theme-nav-item {
    width: 40px;
  }

  .cart-nav-item {
    width: 44px;
  }

  .cart-svg {
    width: 20px;
    height: 20px;
  }
}
</style>
